import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { VALIDADOR_DATOS_CHECK_ID_LABEL } from "@/lib/checkIdLabels";
import { DATOS_MAESTROS_CHECK_ID_PATH } from "@/modules/datosMaestros/datosMaestrosHub";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import FieldHint from "@/components/laboral/FieldHint";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import ControlNominasConsultaPanel from "@/components/laboral/ControlNominasConsultaPanel";
import { TrabajadoresExpedientePanel } from "@/pages/laboral/ExpedientesLaborales";
import PlantillasLaboralesPanel from "@/pages/laboral/PlantillasLaboralesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle,
  FileCode,
  FolderOpen,
  Loader2,
  Lock,
  Plus,
  Upload,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  MAX_XML_BYTES,
  collectTooLargeMessages,
  partitionFilesByMaxSize,
} from "@/lib/fileSizeLimits";
import { IdentityValidateButton } from "@/components/identity/IdentityCheckDialog";

const CONTEXT_LABEL = "Asistente Laboral y REPSE";

function filterXmlNominaFiles(fileList) {
  return Array.from(fileList || []).filter((f) => {
    const name = (f.name || "").toLowerCase();
    return name.endsWith(".xml") && !name.startsWith(".");
  });
}

function emptyForm() {
  return {
    nombre_completo: "",
    curp: "",
    rfc: "",
    nss: "",
    correo: "",
    codigo_postal: "",
    registro_patronal_id: "",
    registro_patronal: "",
    id_contrato_asignado: "",
    sdi: "",
    fecha_alta_imss: "",
  };
}

function labelObraRepse(contratoId, contratos) {
  if (!contratoId) return "—";
  const c = contratos.find((x) => x.id === contratoId);
  if (!c) return "—";
  return c.razon_social_obra || c.numero_contrato || "Obra";
}

/**
 * Catálogo de trabajadores: alta/importación y listado con expediente documental.
 */
export default function CatalogoTrabajadores() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("tab") === "registrados"
      ? "registrados"
      : searchParams.get("tab") === "plantillas"
        ? "plantillas"
        : searchParams.get("tab") === "nomina"
          ? "nomina"
          : "alta";
  const workerOpenId = searchParams.get("open") || "";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [patronales, setPatronales] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPatronalId, setImportPatronalId] = useState("");
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const excelInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const xmlInputRef = useRef(null);
  const xmlFolderInputRef = useRef(null);
  const [syncAnio, setSyncAnio] = useState(() => new Date().getFullYear());
  const [dragOverPdf, setDragOverPdf] = useState(false);
  const [dragOverXml, setDragOverXml] = useState(false);
  const [tooLargeOpen, setTooLargeOpen] = useState(false);
  const [tooLargeItems, setTooLargeItems] = useState([]);

  const hints = config?.field_hints || {};
  const visorSatActivo = Boolean(config?.modulo_visor_sat ?? config?.visor_sat_sync_disponible);
  const modalidadMsg = config?.mensaje_modalidad || "";

  const goToRegistrados = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "registrados");
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const onTabChange = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "registrados") next.set("tab", "registrados");
    else if (tab === "plantillas") next.set("tab", "plantillas");
    else if (tab === "nomina") next.set("tab", "nomina");
    else next.delete("tab");
    next.delete("open");
    setSearchParams(next, { replace: true });
  };

  const goToControlNomina = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "nomina");
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const goToAlta = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadConfig = useCallback(async () => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    try {
      const { data } = await api.get("/laboral/catalogo-trabajadores/config", {
        params: { client_id: clientId },
      });
      setConfig(data);
    } catch {
      setConfig(null);
    }
  }, [clientId]);

  const loadPatronales = useCallback(async () => {
    if (!clientId) {
      setPatronales([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/registros-patronales", {
        params: { client_id: clientId },
      });
      setPatronales(Array.isArray(data.items) ? data.items : []);
    } catch {
      setPatronales([]);
    }
  }, [clientId]);

  const loadContratos = useCallback(async () => {
    if (!clientId) {
      setContratos([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/contratos-repse", {
        params: { client_id: clientId },
      });
      setContratos(Array.isArray(data.items) ? data.items : []);
    } catch {
      setContratos([]);
    }
  }, [clientId]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    loadConfig();
    loadPatronales();
    loadContratos();
    setForm(emptyForm());
  }, [clientId, loadConfig, loadPatronales, loadContratos]);

  const payloadFromForm = (f) => ({
    client_id: clientId,
    nombre_completo: f.nombre_completo.trim(),
    curp: f.curp.trim().toUpperCase(),
    rfc: f.rfc.trim().toUpperCase(),
    nss: f.nss.trim(),
    correo: f.correo.trim(),
    codigo_postal: f.codigo_postal.trim(),
    registro_patronal_id: f.registro_patronal_id || "",
    registro_patronal: f.registro_patronal.trim(),
    id_contrato_asignado: f.id_contrato_asignado || "",
    sdi: f.sdi.trim(),
    fecha_alta_imss: f.fecha_alta_imss || "",
  });

  const onCreate = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente de trabajo");
      return;
    }
    if (!form.nombre_completo.trim() && !form.curp.trim()) {
      toast.error("Indique nombre completo o CURP");
      return;
    }
    setBusy(true);
    try {
      await api.post("/laboral/catalogo-trabajadores", payloadFromForm(form));
      toast.success("Trabajador registrado en el catálogo");
      setForm(emptyForm());
      goToRegistrados();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al registrar trabajador");
    } finally {
      setBusy(false);
    }
  };

  const onImportarExterno = async (excelFile, xmlFiles, meta = {}) => {
    if (!clientId || (!excelFile && (!xmlFiles || !xmlFiles.length))) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      if (excelFile) body.append("archivo_excel", excelFile);
      (xmlFiles || []).forEach((f) => body.append("archivos_xml", f));
      const { data } = await api.post("/laboral/catalogo-trabajadores/importar-externo/preview", body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setImportPatronalId(form.registro_patronal_id || "");
      const previewType = meta.origen || (excelFile ? "excel" : "xml_archivos");
      setImportPreview({
        type: previewType,
        items: Array.isArray(data.items) ? data.items : [],
        count: data.count ?? 0,
        nota: data.nota || "",
        xml_count: xmlFiles?.length || 0,
        xmlFiles: xmlFiles || [],
        origen: meta.origen || previewType,
      });
      const origenLabel = excelFile
        ? "Excel"
        : meta.label || `${xmlFiles?.length || 0} archivo(s) XML`;
      toast.success(`Detectados ${data.count || 0} trabajador(es) desde ${origenLabel}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "FILE_TOO_LARGE") {
        setTooLargeItems(collectTooLargeMessages([], [detail]));
        setTooLargeOpen(true);
      } else {
        toast.error(formatApiError(detail) || "No se pudo importar Excel/XML");
      }
    } finally {
      setBusy(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
      if (xmlInputRef.current) xmlInputRef.current.value = "";
      if (xmlFolderInputRef.current) xmlFolderInputRef.current.value = "";
    }
  };

  const onXmlFilesSelected = (fileList) => {
    const xmls = filterXmlNominaFiles(fileList);
    if (!xmls.length) {
      toast.error("Seleccione al menos un archivo .xml de nómina (CFDI tipo N)");
      return;
    }
    const { accepted, rejected } = partitionFilesByMaxSize(xmls, MAX_XML_BYTES);
    if (rejected.length) {
      setTooLargeItems(collectTooLargeMessages(rejected, []));
      setTooLargeOpen(true);
    }
    if (!accepted.length) return;
    onImportarExterno(null, accepted, {
      origen: "xml_archivos",
      label: `${accepted.length} archivo(s) XML`,
    });
  };

  const onXmlFolderSelected = (fileList) => {
    const xmls = filterXmlNominaFiles(fileList);
    if (!xmls.length) {
      toast.error("La carpeta seleccionada no contiene archivos .xml de nómina");
      return;
    }
    const { accepted, rejected } = partitionFilesByMaxSize(xmls, MAX_XML_BYTES);
    if (rejected.length) {
      setTooLargeItems(collectTooLargeMessages(rejected, []));
      setTooLargeOpen(true);
    }
    if (!accepted.length) return;
    onImportarExterno(null, accepted, {
      origen: "xml_carpeta",
      label: `${accepted.length} XML en carpeta`,
    });
  };

  const onImportarPdfs = async (files) => {
    if (!clientId || !files?.length) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      Array.from(files).forEach((f) => body.append("archivos_pdf", f));
      const { data } = await api.post("/laboral/catalogo-trabajadores/importar-pdfs/preview", body, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setImportPatronalId(form.registro_patronal_id || "");
      setImportPreview({
        type: "pdfs",
        items: Array.isArray(data.items) ? data.items : [],
        count: data.count ?? 0,
        nota: data.nota || "",
      });
      toast.success(`Extraídos ${data.count || 0} trabajador(es) de ${files.length} PDF(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron procesar los PDFs");
    } finally {
      setBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const onSincronizarVisorSat = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      const { data } = await api.get("/laboral/catalogo-trabajadores/sincronizar-visor-sat", {
        params: { client_id: clientId, anio: syncAnio },
      });
      setImportPatronalId(form.registro_patronal_id || "");
      setImportPreview({
        type: "visor_sat",
        items: Array.isArray(data.items) ? data.items : [],
        count: data.count ?? 0,
        nota: data.nota || "",
        cfdi_nomina_encontrados: data.cfdi_nomina_encontrados ?? 0,
        anio: data.anio ?? syncAnio,
      });
      if (!(data.items || []).length) {
        toast.warning("No se encontraron CFDI de nómina emitidos para el periodo seleccionado");
      } else {
        toast.success(
          `Detectados ${data.count || 0} trabajador(es) en ${data.cfdi_nomina_encontrados || 0} CFDI de nómina`,
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar desde Visor SAT");
    } finally {
      setBusy(false);
    }
  };

  const onConfirmImport = async () => {
    if (!clientId || !importPreview?.items?.length) return;
    const isXmlImport = importPreview.type === "xml_archivos" || importPreview.type === "xml_carpeta";
    const endpoint = importPreview.type === "pdf" || importPreview.type === "pdfs"
      ? "/laboral/catalogo-trabajadores/importar-pdf/confirmar"
      : isXmlImport
        ? "/laboral/catalogo-trabajadores/importar-externo/confirmar"
        : "/laboral/catalogo-trabajadores/importar-excel/confirmar";
    setBusy(true);
    try {
      let data;
      if (isXmlImport && importPreview.xmlFiles?.length) {
        const body = new FormData();
        body.append("client_id", clientId);
        body.append("registro_patronal_id", importPatronalId || "");
        body.append("items_json", JSON.stringify(importPreview.items));
        body.append("origen", importPreview.origen || importPreview.type || "xml_import");
        importPreview.xmlFiles.forEach((f) => body.append("archivos_xml", f));
        ({ data } = await api.post(endpoint, body, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
        }));
      } else {
        ({ data } = await api.post(endpoint, {
          client_id: clientId,
          registro_patronal_id: importPatronalId || "",
          items: importPreview.items,
        }));
      }
      const creados = data.created_count ?? data.creados ?? 0;
      const actualizados = data.updated_count ?? data.actualizados ?? 0;
      const recibos = (data.recibos_inserted_count ?? 0) + (data.recibos_updated_count ?? 0);
      const msgRecibos = recibos > 0 ? ` · ${recibos} recibo(s) de nómina guardado(s)` : "";
      toast.success(
        `Importación completada: ${creados} nuevo(s), ${actualizados} actualizado(s)${msgRecibos}`,
      );
      setImportPreview(null);
      setConfirmImportOpen(false);
      goToRegistrados();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al confirmar importación");
    } finally {
      setBusy(false);
    }
  };

  const importPatronalSelectValue = importPatronalId || "__none__";

  const renderWorkerFields = (values, setValues, idPrefix = "") => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor={`${idPrefix}nombre`}>Nombre completo</Label>
        <FieldHint>{hints.nombre_completo}</FieldHint>
        <Input
          id={`${idPrefix}nombre`}
          value={values.nombre_completo}
          onChange={(e) => setValues((p) => ({ ...p, nombre_completo: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}curp`}>CURP</Label>
        <FieldHint>{hints.curp}</FieldHint>
        <div className="flex gap-2 items-center">
          <Input
            id={`${idPrefix}curp`}
            value={values.curp}
            onChange={(e) => setValues((p) => ({ ...p, curp: e.target.value.toUpperCase() }))}
            disabled={!clientId || busy}
            className="font-mono text-sm flex-1"
          />
          <IdentityValidateButton curp={values.curp} label="Validar CURP" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}rfc`}>RFC</Label>
        <FieldHint>{hints.rfc}</FieldHint>
        <div className="flex gap-2 items-center">
          <Input
            id={`${idPrefix}rfc`}
            value={values.rfc}
            onChange={(e) => setValues((p) => ({ ...p, rfc: e.target.value.toUpperCase() }))}
            disabled={!clientId || busy}
            className="font-mono text-sm flex-1"
          />
          <IdentityValidateButton rfc={values.rfc} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}nss`}>NSS</Label>
        <FieldHint>{hints.nss}</FieldHint>
        <Input
          id={`${idPrefix}nss`}
          value={values.nss}
          onChange={(e) => setValues((p) => ({ ...p, nss: e.target.value }))}
          disabled={!clientId || busy}
          className="font-mono text-sm"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}correo`}>Correo</Label>
        <FieldHint>{hints.correo}</FieldHint>
        <Input
          id={`${idPrefix}correo`}
          type="email"
          value={values.correo}
          onChange={(e) => setValues((p) => ({ ...p, correo: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}cp`}>Código postal</Label>
        <FieldHint>{hints.codigo_postal}</FieldHint>
        <Input
          id={`${idPrefix}cp`}
          value={values.codigo_postal}
          onChange={(e) => setValues((p) => ({ ...p, codigo_postal: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label>Registro patronal</Label>
        <FieldHint>{hints.registro_patronal_id}</FieldHint>
        <Select
          value={values.registro_patronal_id || ""}
          onValueChange={(v) => {
            const patron = patronales.find((p) => p.id === v);
            setValues((p) => ({
              ...p,
              registro_patronal_id: v,
              registro_patronal: patron?.registro_patronal || p.registro_patronal,
            }));
          }}
          disabled={!clientId || busy || patronales.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={patronales.length ? "Seleccione patrón" : "Sin patrones activos"} />
          </SelectTrigger>
          <SelectContent>
            {patronales.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.registro_patronal} — {p.nombre_sucursal_obra || p.razon_social_imss || "Patrón"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Obra / contrato REPSE (opcional)</Label>
        <FieldHint>{hints.id_contrato_asignado}</FieldHint>
        <Select
          value={values.id_contrato_asignado || "__none__"}
          onValueChange={(v) =>
            setValues((p) => ({ ...p, id_contrato_asignado: v === "__none__" ? "" : v }))
          }
          disabled={!clientId || busy}
        >
          <SelectTrigger>
            <SelectValue placeholder="Sin obra asignada" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Sin obra asignada</SelectItem>
            {contratos.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.numero_contrato} — {c.razon_social_obra || "Obra"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}sdi`}>SDI</Label>
        <FieldHint>{hints.sdi}</FieldHint>
        <Input
          id={`${idPrefix}sdi`}
          value={values.sdi}
          onChange={(e) => setValues((p) => ({ ...p, sdi: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}fecha-alta`}>Fecha alta IMSS</Label>
        <FieldHint>{hints.fecha_alta_imss}</FieldHint>
        <Input
          id={`${idPrefix}fecha-alta`}
          type="date"
          value={values.fecha_alta_imss}
          onChange={(e) => setValues((p) => ({ ...p, fecha_alta_imss: e.target.value }))}
          disabled={!clientId || busy}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="catalogo-trabajadores-page">
      <PageHeader
        title="Catálogo de Trabajadores"
        subtitle={CONTEXT_LABEL}
        description={
          config?.instrucciones ||
          "Registre o importe la plantilla del cliente. Consulte expedientes, contratos y documentos en Trabajadores registrados."
        }
      />

      <LaboralFlujoOperativo>
      <div className="space-y-6">
      <p className="text-xs text-slate-600">
        Validación RFC/CURP, NSS e inscripciones:{" "}
        <Link
          to={DATOS_MAESTROS_CHECK_ID_PATH}
          className="text-[#0F2D5C] underline font-medium"
        >
          {VALIDADOR_DATOS_CHECK_ID_LABEL}
        </Link>
        .
      </p>

      <section className="rounded-lg border bg-card p-4 md:p-6">
        <div className="space-y-1 max-w-xl">
          <Label className="text-xs text-muted-foreground">Cliente de trabajo</Label>
          <Select value={clientId || undefined} onValueChange={setClientId}>
            <SelectTrigger data-testid="select-cliente-catalogo-trabajadores">
              <SelectValue placeholder="Seleccione cliente" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.razon_social} — {c.rfc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {!clientId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Seleccione un cliente de trabajo para administrar el catálogo.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-4">
        <div
          className="sticky top-0 z-30 -mx-1 px-1 pb-3 pt-1 bg-[#F4F6F8]/95 backdrop-blur-sm border-b border-slate-200 shadow-sm"
          data-testid="catalogo-tabs-sticky"
        >
          <TabsList className="h-auto flex flex-wrap w-full gap-1 bg-white p-1.5 border border-slate-200 rounded-lg shadow-sm">
            <TabsTrigger
              value="alta"
              data-testid="tab-catalogo-alta"
              className="flex-1 min-w-[120px] data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
            >
              Alta de trabajadores
            </TabsTrigger>
            <TabsTrigger
              value="nomina"
              data-testid="tab-catalogo-nominas"
              className="flex-1 min-w-[120px] data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
            >
              Control de Nóminas
            </TabsTrigger>
            <TabsTrigger
              value="plantillas"
              data-testid="tab-catalogo-plantillas"
              className="flex-1 min-w-[120px] data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
            >
              Contratos prediseñados
            </TabsTrigger>
            <TabsTrigger
              value="registrados"
              data-testid="tab-catalogo-registrados"
              className="flex-1 min-w-[120px] font-semibold data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white data-[state=inactive]:text-[#0F2D5C] data-[state=inactive]:border data-[state=inactive]:border-[#0F2D5C]/30"
            >
              Trabajadores registrados
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="alta" className="space-y-6 mt-4">
      {clientId ? (
        <div
          className="rounded-md border border-sky-200 bg-sky-50/90 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="aviso-alta-a-registrados"
        >
          <p className="text-sm text-sky-950 m-0">
            Para <strong>importar desde nómina</strong> (XML, Excel, PDF o Visor SAT), use la pestaña{" "}
            <strong>Control de Nóminas</strong>. Para <strong>contratos</strong>, <strong>renuncias</strong> y ZIP, vaya a{" "}
            <strong>Trabajadores registrados</strong>.
          </p>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-[#0F2D5C] text-[#0F2D5C]"
              onClick={goToControlNomina}
              data-testid="btn-ir-control-nominas"
            >
              Control de Nóminas
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              onClick={goToRegistrados}
              data-testid="btn-ir-trabajadores-registrados"
            >
              Trabajadores registrados
            </Button>
          </div>
        </div>
      ) : null}
      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4" />
          Alta manual de trabajador
        </div>
        {renderWorkerFields(form, setForm, "new-")}
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            onClick={onCreate}
            disabled={!clientId || busy}
            data-testid="btn-alta-trabajador-catalogo"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Registrar trabajador
          </Button>
        </div>
      </section>
        </TabsContent>

        <TabsContent value="nomina" className="space-y-6 mt-4">
      <ControlNominasConsultaPanel clientId={clientId} patronales={patronales} />

      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4" data-testid="seccion-importar-nominas">
        <h3 className="text-sm font-medium">Cargar nóminas al catálogo</h3>
        <p className="text-sm text-muted-foreground">
          Cargue trabajadores desde su equipo (XML, Excel o PDF) o, si tiene licencia, sincronice CFDI de nómina
          ya integrados en Visor SAT. Revise la vista previa antes de confirmar el alta.
        </p>

        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
          <Label className="text-xs font-semibold text-[#0F2D5C]">
            Desde su equipo (sin Visor SAT)
          </Label>
          <p className="text-[11px] text-slate-600 m-0">
            Suba CFDI de nómina (.xml) desde archivos sueltos o una carpeta completa, un Excel cuatrimestral o
            recibos en PDF. No requiere módulo Visor SAT ni descarga previa al sistema.
          </p>

          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full h-10 justify-start font-semibold border-[#0F2D5C] text-[#0F2D5C] bg-white hover:bg-slate-50"
              disabled={!clientId || busy}
              onClick={() => excelInputRef.current?.click()}
              data-testid="btn-importar-externo-excel-catalogo"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Upload className="w-4 h-4 mr-2 shrink-0" />}
              Seleccionar Excel de nómina
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-10 justify-start font-semibold border-[#0F2D5C] text-[#0F2D5C] bg-white hover:bg-slate-50"
              disabled={!clientId || busy}
              onClick={() => xmlInputRef.current?.click()}
              data-testid="btn-importar-xml-nomina-catalogo"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <FileCode className="w-4 h-4 mr-2 shrink-0" />}
              Seleccionar archivos XML de nómina
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-10 justify-start font-semibold border-[#0F2D5C] text-[#0F2D5C] bg-white hover:bg-slate-50"
              disabled={!clientId || busy}
              onClick={() => xmlFolderInputRef.current?.click()}
              data-testid="btn-importar-xml-carpeta-nomina-catalogo"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <FolderOpen className="w-4 h-4 mr-2 shrink-0" />}
              Seleccionar carpeta XML de nómina
            </Button>
          </div>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            disabled={!clientId || busy}
            onChange={(e) => {
              const excel = e.target.files?.[0] || null;
              if (excel) onImportarExterno(excel, null, { origen: "excel", label: "Excel" });
            }}
          />
          <input
            ref={xmlInputRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            multiple
            className="hidden"
            disabled={!clientId || busy}
            onChange={(e) => onXmlFilesSelected(e.target.files)}
          />
          <input
            ref={xmlFolderInputRef}
            type="file"
            accept=".xml,application/xml,text/xml"
            multiple
            webkitdirectory=""
            directory=""
            className="hidden"
            disabled={!clientId || busy}
            onChange={(e) => onXmlFolderSelected(e.target.files)}
          />

          <div
            role="button"
            tabIndex={0}
            className={`rounded-md border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
              dragOverXml
                ? "border-[#0F2D5C] bg-sky-50"
                : "border-slate-300 bg-white hover:border-slate-400"
            }`}
            data-testid="dropzone-xml-nomina-catalogo"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverXml(true);
            }}
            onDragLeave={() => setDragOverXml(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOverXml(false);
              onXmlFilesSelected(e.dataTransfer.files);
            }}
            onClick={() => !busy && clientId && xmlInputRef.current?.click()}
            onKeyDown={(e) => e.key === "Enter" && xmlInputRef.current?.click()}
          >
            <FileCode className="w-5 h-5 mx-auto mb-1 text-[#0F2D5C]" />
            <p className="font-medium text-slate-700 m-0">Arrastre aquí archivos XML de nómina (CFDI tipo N)</p>
            <p className="text-[10px] text-slate-500 mt-1 m-0">
              También puede elegir una carpeta con el botón «XML — carpeta»
            </p>
          </div>

          <div
            role="button"
            tabIndex={0}
            className={`rounded-md border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
              dragOverPdf
                ? "border-emerald-500 bg-emerald-50"
                : "border-slate-300 bg-white hover:border-slate-400"
            }`}
            data-testid="dropzone-pdf-nomina-catalogo"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverPdf(true);
            }}
            onDragLeave={() => setDragOverPdf(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOverPdf(false);
              const pdfs = Array.from(e.dataTransfer.files || []).filter((f) =>
                f.name.toLowerCase().endsWith(".pdf"),
              );
              if (pdfs.length) onImportarPdfs(pdfs);
            }}
            onClick={() => !busy && clientId && pdfInputRef.current?.click()}
            onKeyDown={(e) => e.key === "Enter" && pdfInputRef.current?.click()}
          >
            <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
            <p className="font-medium text-slate-700 m-0">Arrastre aquí PDFs de nómina</p>
            <p className="text-[10px] text-slate-500 mt-1 m-0">
              RFC, NSS, días e ingresos (extracción inteligente)
            </p>
          </div>
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf"
            multiple
            className="hidden"
            disabled={!clientId || busy}
            onChange={(e) => onImportarPdfs(e.target.files)}
          />
        </div>

        <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
          <Label className="text-xs font-semibold text-[#0F2D5C]">Origen de datos — Visor SAT (licencia)</Label>
          {visorSatActivo ? (
            <p className="text-[11px] text-emerald-800 m-0">
              🟢 {modalidadMsg || "Visor SAT activo: puede sincronizar CFDI de nómina ya cargados en el sistema."}
            </p>
          ) : (
            <p className="text-[11px] text-slate-600 m-0">
              ⚪ {modalidadMsg || "Visor SAT no incluido en su licencia: use la sección anterior (XML, Excel o PDF desde su equipo)."}
            </p>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-slate-500">Año CFDI nómina</Label>
              <Input
                type="number"
                min={2000}
                max={2100}
                value={syncAnio}
                onChange={(e) => setSyncAnio(Number(e.target.value) || new Date().getFullYear())}
                className="h-9 w-28 text-xs"
                disabled={!clientId || busy}
              />
            </div>
            <Button
              type="button"
              className="h-10 bg-[#3DA873] hover:bg-[#349966] font-semibold text-white"
              disabled={!clientId || busy || !visorSatActivo}
              onClick={onSincronizarVisorSat}
              data-testid="btn-sincronizar-visor-sat-catalogo"
              title={!visorSatActivo ? "Requiere licencia Visor SAT activa" : undefined}
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : !visorSatActivo ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <span className="mr-2">📥</span>
              )}
              Sincronizar nóminas desde Visor SAT
            </Button>
          </div>
        </div>
      </section>
        </TabsContent>

        <TabsContent value="plantillas" className="mt-4">
          <PlantillasLaboralesPanel clientId={clientId} patronales={patronales} />
        </TabsContent>

        <TabsContent value="registrados" className="mt-4">
          <TrabajadoresExpedientePanel
            clientId={clientId}
            setClientId={setClientId}
            clients={clients}
            showClientSelector={false}
            showInstructions={false}
            initialWorkerId={workerOpenId}
            onGoToAlta={goToAlta}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(importPreview)} onOpenChange={(open) => !open && setImportPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Vista previa — importación{" "}
              {importPreview?.type === "visor_sat"
                ? "Visor SAT"
                : importPreview?.type === "pdfs"
                  ? "PDFs de nómina"
                  : importPreview?.type === "xml_carpeta"
                    ? `XML de carpeta (${importPreview?.xml_count || 0} archivo(s))`
                    : importPreview?.type === "xml_archivos"
                      ? `XML (${importPreview?.xml_count || 0} archivo(s))`
                      : importPreview?.type === "excel"
                        ? "Excel de nómina"
                        : importPreview?.type === "externo"
                          ? "Excel / XML"
                          : importPreview?.type === "pdf"
                            ? "PDF"
                            : "Excel"}
            </DialogTitle>
          </DialogHeader>
          {importPreview?.nota ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {importPreview.nota}
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            {importPreview?.count ?? importPreview?.items?.length ?? 0} fila(s) detectada(s).
            {importPreview?.type === "visor_sat" && importPreview?.cfdi_nomina_encontrados
              ? ` (${importPreview.cfdi_nomina_encontrados} CFDI de nómina en ${importPreview.anio || syncAnio}).`
              : importPreview?.type === "xml_carpeta" || importPreview?.type === "xml_archivos"
                ? ` Procesados ${importPreview?.xml_count || 0} XML de nómina desde su equipo.`
                : " "}
            Asigne el registro patronal por defecto para filas sin patrón en el archivo.
          </p>
          <div className="space-y-2 max-w-md">
            <Label>Registro patronal para importación</Label>
            <Select
              value={importPatronalSelectValue}
              onValueChange={(v) => setImportPatronalId(v === "__none__" ? "" : v)}
              disabled={busy}
            >
              <SelectTrigger>
                <SelectValue placeholder="Opcional si el archivo trae RP" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin asignar (usar datos del archivo)</SelectItem>
                {patronales.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.registro_patronal} — {p.nombre_sucursal_obra || "Patrón"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!importPreview?.items?.length ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No se detectaron filas válidas en el archivo.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border max-h-64">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>CURP</TableHead>
                    <TableHead>RFC</TableHead>
                    <TableHead>NSS</TableHead>
                    <TableHead>SDI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importPreview.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.nombre_completo || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{item.curp || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{item.rfc || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{item.nss || "—"}</TableCell>
                      <TableCell>{item.sdi || item.sueldo || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportPreview(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={() => setConfirmImportOpen(true)}
              disabled={busy || !importPreview?.items?.length}
              data-testid="btn-confirmar-importacion-trabajadores"
            >
              <Upload className="h-4 w-4 mr-2" />
              Confirmar importación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
      </LaboralFlujoOperativo>

      <AlertDialog open={confirmImportOpen} onOpenChange={setConfirmImportOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar importación al catálogo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se procesarán {importPreview?.items?.length ?? 0} fila(s){" "}
              {importPreview?.type === "visor_sat"
                ? "desde CFDI de nómina del Visor SAT"
                : importPreview?.type === "pdfs"
                  ? "extraídos de PDFs de recibo"
                  : importPreview?.type === "xml_carpeta"
                    ? "desde XML de nómina en carpeta local"
                    : importPreview?.type === "xml_archivos"
                      ? "desde archivos XML de nómina"
                      : importPreview?.type === "excel"
                        ? "desde Excel de nómina"
                        : importPreview?.type === "externo"
                          ? "desde Excel o XML de nómina"
                          : importPreview?.type === "pdf"
                            ? "del PDF"
                            : "del Excel"}.
              {importPatronalId
                ? " Se asignará el registro patronal seleccionado a filas sin patrón."
                : " Se usarán los datos del archivo cuando incluyan registro patronal."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmImport} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Importar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={tooLargeOpen} onOpenChange={setTooLargeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              Archivos omitidos por tamaño
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-700">
                <p>Los siguientes archivos superan el límite de 1 MB (XML) y no fueron procesados:</p>
                <ul className="list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
                  {tooLargeItems.map((item) => (
                    <li key={item.filename + item.message}>{item.message}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Entendido</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
